import { getPlainText } from "../StoreUtils/storePackageUtils";

const textOf = (value) => getPlainText(String(value ?? "")).trim();

const isPlaceholder = (value) =>
  !textOf(value) || /^(na|n\/a|null|undefined|none|-)$/i.test(textOf(value));

const asArray = (value) => (Array.isArray(value) ? value : []);

const isOn = (value) => value === true || value === 1 || value === "1";

const pick = (source, keys) => {
  if (!source || typeof source !== "object") return "";
  for (const key of keys) {
    const value = source[key];
    if (value == null || value === "") continue;
    return value;
  }
  return "";
};

const normalizeTestimonial = (item, index) => {
  if (!item || typeof item !== "object") return null;
  const name = textOf(pick(item, ["student_name", "name", "user_name"]));
  const quote = textOf(
    pick(item, ["testimonials", "testimonial", "review", "feedback", "comment", "text"])
  );
  const air = textOf(pick(item, ["AIR", "air", "rank_label", "rank"]));
  const rank = !air
    ? ""
    : /^air\b/i.test(air)
      ? air.replace(/^air\b/i, "AIR")
      : `AIR ${air}`;

  if (!name && !quote) return null;

  return {
    id: item.id ?? item.student_id ?? `${name}-${index}`,
    name,
    quote,
    rank,
    image: pick(item, ["image", "photo", "avatar", "profile_image", "student_image"]),
  };
};

const normalizeFeature = (item, index) => {
  if (typeof item === "string") {
    const title = textOf(item);
    return title ? { id: `${title}-${index}`, title, description: "", icon: "", type: "" } : null;
  }
  if (!item || typeof item !== "object") return null;

  const title = textOf(
    pick(item, ["title", "title_tag", "heading", "feature_name", "label", "name"])
  );
  const rawDescription = textOf(
    pick(item, ["description", "subtitle", "sub_title", "detail", "text"])
  );
  const name = textOf(item.name);
  const description =
    rawDescription && rawDescription !== title
      ? rawDescription
      : name && name !== title
        ? name
        : "";

  if (!title && !description) return null;

  return {
    id: item.id ?? `${title}-${index}`,
    title: title || description,
    description: title ? description : "",
    icon: pick(item, ["icon", "icon_url", "image"]),
    type: textOf(pick(item, ["type", "key", "slug"])),
  };
};

const normalizeStat = (item, index) => {
  if (!item || typeof item !== "object") return null;
  const value = textOf(pick(item, ["value", "count", "stat", "number"]));
  const label = textOf(pick(item, ["label", "name", "title", "description"]));
  if (isPlaceholder(value) || isPlaceholder(label) || value === label) return null;
  return { id: item.id ?? `${label}-${index}`, value, label };
};

const sectionFrom = (block, fallbackTitle) => {
  if (!block) return null;
  const list = Array.isArray(block)
    ? block
    : asArray(block.features || block.items || block.list);
  const features = list.map(normalizeFeature).filter(Boolean);
  if (!features.length) return null;

  return {
    id: `${fallbackTitle}-${features.length}`,
    title: (!Array.isArray(block) && textOf(block.title)) || fallbackTitle,
    subtitle: !Array.isArray(block) ? textOf(block.subtitle) : "",
    features,
  };
};

const normalizeVideo = (item, index, fallbackTitle) => {
  if (!item || typeof item !== "object") return null;
  const url = textOf(pick(item, ["youtube_url", "video_url", "url", "link", "src"]));
  const videoId = textOf(pick(item, ["video_id", "youtube_id", "id_video", "crypt_id", "id"]));
  const thumbnail = pick(item, ["thumbnail", "thumb", "image", "poster", "banner"]);
  if (!url && !videoId && !thumbnail) return null;

  return {
    id: item.id ?? `${fallbackTitle}-${index}`,
    title: textOf(pick(item, ["title", "name", "heading"])) || fallbackTitle,
    url,
    videoId,
    thumbnail: typeof thumbnail === "string" ? thumbnail : "",
  };
};

const videosFrom = (packageData) => {
  const block = packageData?.youtube_videos;
  if (block && isOn(block.show_youtube_video)) {
    const title = textOf(block.youtube_header) || "About Our Practice";
    const videos = asArray(block.youtube_video_data)
      .map((item, index) => normalizeVideo(item, index, title))
      .filter(Boolean);
    if (videos.length) return videos;
  }

  const about = packageData?.about_video || packageData?.practice_video;
  if (!about) return [];
  if (typeof about === "string" || typeof about === "number") {
    return [
      {
        id: "about-video",
        title: "About Our Practice",
        url: "",
        videoId: String(about),
        thumbnail: "",
      },
    ];
  }
  const video = normalizeVideo(about, 0, "About Our Practice");
  return video ? [video] : [];
};

export const buildCourseStory = (packageData) => {
  if (!packageData || typeof packageData !== "object") {
    return {
      videos: [],
      featureSections: [],
      testimonials: [],
      stats: [],
      hasContent: false,
    };
  }

  const testimonials = asArray(
    packageData.motion_testimonials || packageData.student_testimonials
  )
    .map(normalizeTestimonial)
    .filter(Boolean);

  const stats = asArray(packageData.motion_achievement || packageData.course_stats)
    .map(normalizeStat)
    .filter(Boolean);

  const courseBenefits =
    packageData?.PackageInstallmentDetails?.course_benefits ||
    packageData?.package_details?.course_benefits;

  const featureSections = [
    sectionFrom(packageData.what_you_get, "What You’ll Get") ||
      sectionFrom(courseBenefits, "What You’ll Get"),
  ].filter(Boolean);

  const videos = videosFrom(packageData);
  const hasContent = Boolean(
    videos.length || featureSections.length || testimonials.length || stats.length
  );

  return { videos, featureSections, testimonials, stats, hasContent };
};
